import React from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

interface CommercialDueDiligenceData {
  reputationalConcerns: string;
  reputationalConcernsDetails: string;
  searchTermsTools: string;
  experienceCredentials: string;
}

interface CommercialDueDiligenceFormProps {
  data: CommercialDueDiligenceData;
  onChange: (data: CommercialDueDiligenceData) => void;
  disabled?: boolean;
  estimatedContractAmount?: string;
  errors?: { field: string; message: string }[];
}

const CommercialDueDiligenceForm: React.FC<CommercialDueDiligenceFormProps> = ({ data, onChange, disabled = false, estimatedContractAmount = '0', errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const handleChange = (field: keyof CommercialDueDiligenceData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
        Please review the vendor's public profile, such as their website, recent news mentions, and customer reviews, to assess their reputation and stability.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl component="fieldset" disabled={disabled} error={!!getFieldError('reputationalConcerns')}>
          <FormLabel component="legend">
            Did you identify any fraud, litigation, regulatory action, or serious reputational concerns?
          </FormLabel>
          <RadioGroup
            value={data.reputationalConcerns}
            onChange={handleChange('reputationalConcerns')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('reputationalConcerns') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('reputationalConcerns')}
            </Typography>
          )}
        </FormControl>

        {data.reputationalConcerns === 'YES' && (
          <TextField
            required
            fullWidth
            multiline
            rows={4}
            label="If YES, please explain"
            value={data.reputationalConcernsDetails}
            onChange={handleChange('reputationalConcernsDetails')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('reputationalConcernsDetails')}
            helperText={getFieldError('reputationalConcernsDetails')}
          />
        )}

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Please provide the details of what you did to complete your review"
          value={data.searchTermsTools}
          onChange={handleChange('searchTermsTools')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('searchTermsTools')}
          helperText={getFieldError('searchTermsTools')}
        />

        <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
          Please explain in detail how it was determined the vendor has the experience, credentials, capacity, and materials to provide the goods or services (site visit, interviews, research, etc.)
        </Typography>
        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Provide details."
          value={data.experienceCredentials}
          onChange={handleChange('experienceCredentials')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('experienceCredentials')}
          helperText={getFieldError('experienceCredentials')}
        />
      </Box>
    </Box>
  );
};

export default CommercialDueDiligenceForm;
